
/**
 * T2-6968 — AP /tables/User.
 *
 * Deliberately uses `getDataTables` WITHOUT overriding `getDataFromApi`, the
 * same way Job.vue does. The mixin fetches one page at a time via
 * getPaginateData and the row count via totalItemsByTable.
 *
 * Hygienist.vue, Location.vue and Shift.vue all override that method to load
 * their whole table up front (getAllPaginateItems with limit 1000 / maxPages
 * 200) and then page client-side. Copying that here would pull all ~66k User
 * rows in ~67 sequential requests — more total load than the single unbounded
 * query T2-6962 removed, just spread out. Keep this component on the mixin.
 *
 * The Reset Password / Unsubscribe actions and the account-status chip are
 * carried over from the `tablename === "User"` branches in Tables.vue, which
 * rendered this page before it got its own component. Dropping them would
 * remove live admin controls — see PR #481 review.
 */
import Vue from "vue";
import { mapState, mapMutations } from "vuex";
import TempStarsTableTitle from "@/components/shared/TempStarsTableTitle";
import UpdateMultipleItems from "../components/UpdateMultipleItems";
import PasswordResetDialog from "@/components/Dialogs/PasswordResetDialog";
import UnsubscribeDialog from "@/components/Dialogs/UnsubscribeDialog";
import { headersInterface } from "@/interfaces/Tables.interfaces";
import { constants } from "@/utils/constants";
import { formatUserAccountStatus } from "@/helpers/adminEnumLabels";
import { startCase } from "lodash";
import { getDataTables } from "@/mixins/getDataTables";

export default Vue.extend({
  name: "User",
  components: {
    TempStarsTableTitle,
    UpdateMultipleItems,
    PasswordResetDialog,
    UnsubscribeDialog
  },
  mixins: [getDataTables],
  computed: {
    ...mapState({
      content: "tableContent"
    }),
    tableHeaders(): Array<headersInterface> {
      const columns = constants.enabledColumns.userTable.map(column => ({
        align: "center",
        text: this.formatUserHeaderText(column),
        value: column,
        // The mixin pages server-side, so v-data-table can only ever sort the
        // 15 rows currently loaded. Offering sort arrows would imply sorting
        // all 11k+ rows. Disabled until the paginator supports ordered
        // columns — see PR #481 review recommendation 2.
        sortable: false
      }));

      columns.push({
        align: "center",
        text: "Actions",
        value: "actions",
        sortable: false,
        width: "220px"
      } as any);

      return columns as Array<headersInterface>;
    },
    formattedContent(): Array<Record<string, any>> {
      // Mirrors Tables.vue: blank cells read as "-" rather than empty space.
      // accountStatus is skipped because it renders as a chip that maps
      // null/undefined to "Unknown" itself.
      return (this.content || []).map((item: Record<string, any>) => {
        const formatted = { ...item };
        Object.keys(formatted).forEach(key => {
          if (key === "accountStatus") return;
          if (!this.isPresent(formatted[key])) {
            formatted[key] = "-";
          }
        });
        return formatted;
      });
    }
  },
  methods: {
    ...mapMutations(["modify"]),
    isPresent(value: any): boolean {
      return !(
        value === null ||
        typeof value === "undefined" ||
        value === "" ||
        value === 0 ||
        (typeof value === "string" && value.trim() === "")
      );
    },
    formatUserHeaderText(label: string): string {
      if (!label) return "";
      const normalized = label
        .toString()
        .replace(/_/g, " ")
        .replace(/id$/i, "Id")
        .replace(/amount$/i, "Amount")
        .replace(/status$/i, "Status");
      const base = startCase(normalized.replace(/([a-z])([A-Z])/g, "$1 $2"));
      return base.replace(/\bId\b/g, "ID").replace(/\bUrl\b/g, "URL");
    },
    isAccountDeleted(status: any): boolean {
      if (status === null || typeof status === "undefined") return false;
      const deletedCode =
        constants.USER_STATUSES && constants.USER_STATUSES.DELETED
          ? constants.USER_STATUSES.DELETED
          : -4;
      return Number(status) === deletedCode;
    },
    getAccountStatusLabel(status: any): string {
      return formatUserAccountStatus(status);
    },
    getAccountStatusColor(status: any): string {
      return this.isAccountDeleted(status) ? "#FB7672" : "#13CA82";
    },
    openPasswordResetDialog(user: Record<string, any>) {
      const dialog = this.$refs.passwordResetDialog as any;
      if (dialog && typeof dialog.openDialog === "function") {
        dialog.openDialog({
          id: user.id,
          email: user.email,
          hygienistId: user.hygienistId,
          dentistId: user.dentistId
        });
      }
    },
    handlePasswordResetSuccess(data: any) {
      this.modify({
        property: "snackbar",
        value: {
          visible: true,
          text: `Password reset successful for user ${data.result.userEmail}`,
          color: "green darken-2"
        }
      });
    },
    openUnsubscribeDialog(user: Record<string, any>) {
      const dialog = this.$refs.unsubscribeDialog as any;
      if (dialog && typeof dialog.openDialog === "function") {
        dialog.openDialog({
          id: user.id,
          email: user.email,
          hygienistId: user.hygienistId,
          dentistId: user.dentistId
        });
      }
    },
    handleUnsubscribeSuccess(data: any) {
      this.modify({
        property: "snackbar",
        value: {
          visible: true,
          text: `User ${data.result.userEmail} unsubscribed from emails successfully`,
          color: "green darken-2"
        }
      });
    }
  }
});
